import { useCallback, useEffect, useState } from "react";
import history from "App/History";
import {
  getOpenModeratorMatchTags,
  resolveModeratorMatchTag,
} from "core/services/Tournament";
import { OpenModeratorTagRow } from "core/models/PlayoffRound";
import { useNotification } from "shared/hooks/useNotification";

type Props = {
  tournamentId: string | undefined;
};

export default function ModeratorMatchRequests({
  tournamentId,
}: Props) {
  const [rows, setRows] = useState<OpenModeratorTagRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const showNotification = useNotification();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOpenModeratorMatchTags({
        tournamentId: tournamentId || undefined,
      });
      if (Array.isArray(data)) {
        setRows(data);
      }
    } catch {
      showNotification({
        title: "Error",
        message: "Could not load moderator requests",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [showNotification, tournamentId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleResolve(messageId: string) {
    setResolvingId(messageId);
    try {
      await resolveModeratorMatchTag(messageId);
      setRows((prev) => prev.filter((r) => r.messageId !== messageId));
      showNotification({
        title: "Resolved",
        message: "Moderator request marked resolved",
        type: "success",
      });
    } catch {
      showNotification({
        title: "Error",
        message: "Could not resolve request",
        type: "error",
      });
    } finally {
      setResolvingId(null);
    }
  }

  function openChat(row: OpenModeratorTagRow) {
    if (!tournamentId) return;
    history.push(
      `/playoff/tournaments/${tournamentId}/match/chat/${row.playoffId}?messageId=${row.messageId}`
    );
  }

  if (!tournamentId) {
    return null;
  }

  return (
    <div className="moderator-requests-panel">
      <div className="moderator-requests-actions">
        <button
          type="button"
          className="moderator-requests-refresh"
          disabled={loading}
          onClick={() => load()}
        >
          Refresh
        </button>
      </div>
      {loading && rows.length === 0 ? (
        <p className="moderator-requests-empty">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="moderator-requests-empty">No open moderator requests.</p>
      ) : (
        <div className="moderator-requests-table-wrap">
          <table className="moderator-requests-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>From</th>
                <th>Preview</th>
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.messageId}>
                  <td>
                    {row.createdAt
                      ? new Date(row.createdAt).toLocaleString()
                      : "—"}
                  </td>
                  <td>{row.senderName || "—"}</td>
                  <td className="moderator-requests-preview">
                    {row.isDeleted ? "[Deleted]" : row.contentPreview}
                  </td>
                  <td>{row.source || "—"}</td>
                  <td className="moderator-requests-actions-cell">
                    <div className="moderator-requests-actions-group">
                      <button
                        type="button"
                        className="moderator-requests-link"
                        onClick={() => openChat(row)}
                      >
                        Open in chat
                      </button>
                      <button
                        type="button"
                        className="moderator-requests-resolve"
                        disabled={resolvingId === row.messageId}
                        onClick={() => handleResolve(row.messageId)}
                      >
                        {resolvingId === row.messageId ? "…" : "Resolve"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
